import { FIXED_ARROW_HEIGHT, FIXED_ITEM_MARGIN, FIXED_MAIN_CATEGORY_HEIGHT } from "constants/constants";
import { IFishboneDiagramProblemCause } from "entities/RCA/RCALibraryEntity/RCALibraryEntityInterfaces";
import { useEffect, useMemo, useRef, useState } from "react";
import { IUseFishboneDiagramCategories } from "./RCAFishboneDiagramCategoryInterfaces";

const UseFishboneDiagramCategories = ({ items, position, onComponentReady }: IUseFishboneDiagramCategories) => {
    const [totalHeight, setTotalHeight] = useState<number>(0);
    const [finalItemsWithPositionValue, setFinalItemsWithPositionValue] = useState<
        (IFishboneDiagramProblemCause & { position: number })[]
    >([]);
    const [dimensions, setDimensions] = useState({});

    const mainItems = useMemo(() => items.filter((item) => !item.primary_problem_cause_id), [items]);

    const itemsRefs = useRef<HTMLDivElement[]>([]);

    const finalItems = useMemo(() => {
        const calculatedFinalItems: IFishboneDiagramProblemCause[] = [];
        mainItems.forEach((mainItem) => {
            calculatedFinalItems.push(mainItem);
            const subItems = items.filter(
                (item) => item.primary_problem_cause_id === mainItem.fishbone_diagram_problem_cause_id,
            );
            calculatedFinalItems.push(...subItems);
        });
        return calculatedFinalItems;
    }, [items, mainItems]);

    useEffect(() => {
        const resizeObserver = new ResizeObserver((entries) => {
            const newDimensionList = {} as any;
            entries.forEach((entry) => {
                const currentElement = entry.target;
                const { height } = entry.contentRect;
                const id = currentElement.getAttribute("data-id");
                newDimensionList[id as string] = { height };
                setDimensions(newDimensionList);
            });
        });

        itemsRefs.current = itemsRefs.current.filter((ref) => ref !== undefined);
        Object.values(itemsRefs.current).forEach((element) => {
            if (element) {
                resizeObserver.observe(element);
            }
        });

        return () => {
            resizeObserver.disconnect();
        };
    }, [finalItems]);

    useEffect(() => {
        const calculateTotalHeight = () => {
            let localTotalItemsHeight = 0;
            itemsRefs.current.forEach((item) => {
                localTotalItemsHeight += item.offsetHeight;
            });

            const totalHeightWithAllItems =
                localTotalItemsHeight +
                FIXED_MAIN_CATEGORY_HEIGHT +
                FIXED_ITEM_MARGIN * (finalItems.length + 1) +
                FIXED_ARROW_HEIGHT;

            setTotalHeight(totalHeightWithAllItems);
        };

        const addPositionToFinalItems = () => {
            const finalItemsArray = [] as any;
            let itemsHeightValue = 0;
            finalItems.forEach((item, index) => {
                if (index === 0) {
                    const topPosition =
                        totalHeight -
                        FIXED_MAIN_CATEGORY_HEIGHT -
                        FIXED_ITEM_MARGIN -
                        itemsRefs.current[index]?.offsetHeight;

                    const bottomPosition =
                        totalHeight - FIXED_ARROW_HEIGHT - FIXED_ITEM_MARGIN - itemsRefs.current[index]?.offsetHeight;

                    finalItemsArray.push({
                        ...item,
                        position: position === "top" ? topPosition : bottomPosition,
                    });
                    itemsHeightValue += itemsRefs.current[index]?.offsetHeight;
                } else {
                    const topPosition =
                        totalHeight -
                        itemsHeightValue -
                        FIXED_MAIN_CATEGORY_HEIGHT -
                        FIXED_ITEM_MARGIN * (index + 1) -
                        itemsRefs.current[index]?.offsetHeight;

                    const bottomPosition =
                        totalHeight -
                        itemsHeightValue -
                        FIXED_ARROW_HEIGHT -
                        FIXED_ITEM_MARGIN * (index + 1) -
                        itemsRefs.current[index]?.offsetHeight;

                    finalItemsArray.push({
                        ...item,
                        position: position === "top" ? topPosition : bottomPosition,
                    });
                    itemsHeightValue += itemsRefs.current[index]?.offsetHeight;
                }
            });
            setFinalItemsWithPositionValue(finalItemsArray);
        };

        calculateTotalHeight();
        addPositionToFinalItems();
        onComponentReady();

        window.addEventListener("resize", calculateTotalHeight);
        window.addEventListener("resize", addPositionToFinalItems);
        return () => {
            window.removeEventListener("resize", calculateTotalHeight);
            window.removeEventListener("resize", addPositionToFinalItems);
        };
    }, [dimensions, finalItems, onComponentReady, position, totalHeight]);

    return {
        totalHeight,
        finalItemsWithPositionValue,
        itemsRefs,
    };
};

export default UseFishboneDiagramCategories;
